import { authErrorText } from '../data/auth';
import { report } from '../sentry';

/** Слова на случай, когда сервер ответил успехом, а экран после него не
 *  обновился. Место вызова задаёт свои, если «вход» по смыслу неверен. */
const ПРОДОЛЖЕНИЕ_УПАЛО = 'Вход выполнен, но экран не открылся. Перезагрузите страницу.';

/** Те же слова для действий, после которых никто не входил: письмо ушло,
 *  запись заведена, а экран не сдвинулся. */
export const ГОТОВО_БЕЗ_ЭКРАНА = 'Готово, но экран не обновился. Перезагрузите страницу.';

/** Граница одного действия с сервером входа: занять экран, позвать, снять
 *  занятость ПРИ ЛЮБОМ исходе и сказать родителю, что стало.
 *
 *  Зачем отдельная граница, а не аккуратность в каждом месте. Библиотека
 *  ВОЗВРАЩАЕТ в `{ error }` только `AuthError`, а всё прочее перебрасывает —
 *  прочитано в `node_modules/@supabase/auth-js/dist/main/GoTrueClient.js`:
 *  `if (isAuthError(error)) return { data: null, error }; throw error;`.
 *  Каждое действие экрана входа было написано как
 *  `void auth.X().then(({ error }) => { setBusy(false); … })` — без
 *  обработчика отказа. На броске такой обработчик не исполняется вовсе:
 *  кнопка остаётся серой навсегда, объяснения нет, выход один —
 *  перезагрузка. Измерено исполнением во всех пяти местах, среди
 *  них восстановление пароля — единственная дорога родителя обратно к
 *  занятиям детей. Пять одинаковых ошибок — это не пять недосмотров, а
 *  отсутствие места, где правило живёт; здесь оно живёт.
 *
 *  Обычный обрыв связи сюда броском НЕ приходит: `AuthRetryableFetchError`
 *  — это `AuthError`, его библиотека возвращает. Бросок — это сбой
 *  хранилища, подменённый `fetch`, внутренняя ошибка библиотеки.
 *
 *  Исходы:
 *  - `{ error }` и бросок переводятся ОДНИМ `authErrorText`: родитель не
 *    должен видеть разные слова в зависимости от того, каким путём пришёл
 *    отказ. Незнакомый бросок в Sentry отправляет сам перевод, один раз;
 *    сетевой не отправляется — метро не поломка;
 *  - успех, после которого упало продолжение (`успех`), — НЕ отказ
 *    сервера. На сервере всё случилось: родитель уже вошёл, код уже ушёл.
 *    Показать ему «не получилось» значит отправить его повторять — входить
 *    снова, слать письма до предела. Поэтому свои слова и своя отметка в
 *    Sentry (`serverAction:продолжение`);
 *  - занятость снимается в `finally`, то есть и тогда, когда сломалось
 *    что-то, о чём здесь не подумали. Долгое продолжение (выход, выгрузка)
 *    держит экран занятым до своего конца: иначе вторую кнопку нажмут
 *    посреди первой.
 *
 *  Сама граница не отвергает никогда: места вызова пишут
 *  `void действие(…)`, и отвергнутое обещание там было бы ровно той
 *  тишиной, которую граница убирает. */
export async function действие<T>(ход: {
  занять: (занят: boolean) => void;
  вызов: () => Promise<{ error: unknown } & T>;
  отказ: (текст: string) => void;
  успех: (ответ: T) => void | Promise<void>;
  /** Что сказать, если сервер ответил успехом, а `успех` бросил. */
  продолжение?: string;
}): Promise<void> {
  ход.занять(true);
  try {
    const ответ = await ход.вызов();
    if (ответ.error) {
      ход.отказ(authErrorText(ответ.error));
      return;
    }
    try {
      await ход.успех(ответ);
    } catch (сбой) {
      report(сбой, 'serverAction:продолжение');
      ход.отказ(ход.продолжение ?? ПРОДОЛЖЕНИЕ_УПАЛО);
    }
  } catch (бросок) {
    ход.отказ(authErrorText(бросок));
  } finally {
    ход.занять(false);
  }
}
